import React, { useState, useEffect } from "react";
import WadeLogo from "../../../../components/Icons/WadeLogo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { changePasswordService } from "../../../../services/changePassword.service";
import PasswordChangeMessage from "../../Message/PasswordChangeMessage/PasswordChangeMessage";
import AuthLayout from "../../AuthLayout";
import CustomLoader from "../../../Loader/Spinner";

// Icons
import Group11 from "../../../../assets/images/svg/group11"; // Eye
import Group7 from "../../../../assets/images/svg/group7"; // Eye

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const regexPasswordPattern = /^.{8,90}$/;

  // State
  const [passwordInput, setPasswordInput] = useState("");
  const [confirmPasswordInput, setConfirmPasswordInput] = useState("");

  // Visibility Toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Errors
  const [createPasswordEmpty, setCreatePasswordEmpty] = useState(false);
  const [confirmPasswordEmpty, setConfirmPasswordEmpty] = useState(false);
  const [createAndConfirmPasswordMismatch, setCreateAndConfirmPasswordMismatch] = useState(false);
  const [passwordCriteriaMismatch, setPasswordCriteriaMismatch] = useState(false);
  const [incorrectTemporaryPassword, setIncorrectTemporaryPassword] = useState(false);

  // UI Inputs Touched State
  const [incorrectcreatePasswordInputChange, setIncorrectcreatePasswordInputChange] = useState(false);
  const [incorrectconfirmPasswordInputChange, setIncorrectconfirmPasswordInputChange] = useState(false);

  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    localStorage.removeItem("isLoggedIn");
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateAndConfirmPasswordMismatch(false);
    setIncorrectTemporaryPassword(false);
    if (createPasswordEmpty) {
      setIncorrectcreatePasswordInputChange(false);
    }
    setIncorrectcreatePasswordInputChange(true);
    setPasswordInput(e.target.value);

    // Reset errors on type if needed
    if (createPasswordEmpty) setCreatePasswordEmpty(false);
    if (passwordCriteriaMismatch) setPasswordCriteriaMismatch(false);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateAndConfirmPasswordMismatch(false);
    setIncorrectTemporaryPassword(false);
    if (createPasswordEmpty) {
      setIncorrectconfirmPasswordInputChange(false);
    }
    setIncorrectconfirmPasswordInputChange(true);
    setConfirmPasswordInput(e.target.value);

    if (confirmPasswordEmpty) setConfirmPasswordEmpty(false);
  };

  const passwordCreationHandler = () => {
    setIncorrectcreatePasswordInputChange(false);
    setIncorrectconfirmPasswordInputChange(false);
    setCreatePasswordEmpty(false);
    setConfirmPasswordEmpty(false);
    setCreateAndConfirmPasswordMismatch(false);
    setPasswordCriteriaMismatch(false);

    let hasError = false;

    if (passwordInput === "") {
      setCreatePasswordEmpty(true);
      hasError = true;
    }

    if (confirmPasswordInput === "") {
      setConfirmPasswordEmpty(true);
      hasError = true;
    }

    if (!hasError) {
      if (passwordInput !== confirmPasswordInput) {
        setCreateAndConfirmPasswordMismatch(true);
        hasError = true;
      } else if (!regexPasswordPattern.test(passwordInput)) {
        setPasswordCriteriaMismatch(true);
        hasError = true;
      }
    }

    if (!hasError) {
      setIsLoading(true);
      changePasswordService(
        location.state?.email,
        location.state?.temporary_password,
        confirmPasswordInput
      )
        .then(() => {
          setIsLoading(false);
          setModalShow(true);
        })
        .catch((res) => {
          setIsLoading(false);
          if (res.status === 401) {
            if (res.resBody?.detail?.includes("Temporary password has expired")) {
              setIncorrectTemporaryPassword(true);
            }
          } else {
            console.error("Change password failed", res);
          }
        });
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // Helper to determine if input has error border
  const hasPasswordError = !incorrectcreatePasswordInputChange && (createPasswordEmpty || passwordCriteriaMismatch);
  const hasConfirmError = !incorrectconfirmPasswordInputChange && (confirmPasswordEmpty || createAndConfirmPasswordMismatch);

  return (
    <AuthLayout>
      <div className="tw-w-full tw-max-w-md tw-flex tw-flex-col tw-items-center">
        {/* Logo */}
        <img
          src={WadeLogo}
          alt="WADE"
          className="tw-h-10 tw-mb-12"
        />

        <h2 className="tw-text-3xl tw-font-semibold tw-text-[#03234a] tw-mb-8">
          Change Password
        </h2>

        <div className="tw-w-full">
          {/* New Password Input */}
          <div className="tw-mb-6 tw-relative">
            <label className="tw-block tw-text-[#03234a] tw-mb-2 tw-text-sm tw-font-medium">
              New Password
            </label>
            <div className="tw-relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={passwordInput}
                onChange={handlePasswordChange}
                disabled={isLoading}
                maxLength={100}
                className={`tw-w-full tw-h-12 tw-px-4 tw-pr-12 tw-rounded-md tw-border tw-text-base focus:tw-outline-none focus:tw-ring-2 tw-transition-colors
                  ${hasPasswordError ? 'tw-border-[#e88181] focus:tw-ring-[#e88181]' : 'tw-border-gray-200 focus:tw-ring-blue-100 focus:tw-border-[#03234a]'}
                `}
              />
              {passwordInput !== "" && (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 focus:tw-outline-none"
                >
                  {showPassword ? <Group7 /> : <Group11 />}
                </button>
              )}
            </div>
            {createPasswordEmpty && <p className="tw-text-[#e88181] tw-text-sm tw-mt-1">New Password cannot be empty.</p>}
            {passwordCriteriaMismatch && <p className="tw-text-[#e88181] tw-text-sm tw-mt-1">Password must be at least 8 characters.</p>}
          </div>

          {/* Confirm Password Input */}
          <div className="tw-mb-8 tw-relative">
            <label className="tw-block tw-text-[#03234a] tw-mb-2 tw-text-sm tw-font-medium">
              Confirm Password
            </label>
            <div className="tw-relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Password"
                value={confirmPasswordInput}
                onChange={handleConfirmPasswordChange}
                disabled={isLoading}
                maxLength={100}
                className={`tw-w-full tw-h-12 tw-px-4 tw-pr-12 tw-rounded-md tw-border tw-text-base focus:tw-outline-none focus:tw-ring-2 tw-transition-colors
                  ${hasConfirmError ? 'tw-border-[#e88181] focus:tw-ring-[#e88181]' : 'tw-border-gray-200 focus:tw-ring-blue-100 focus:tw-border-[#03234a]'}
                `}
              />
              {confirmPasswordInput !== "" && (
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 focus:tw-outline-none"
                >
                  {showConfirmPassword ? <Group7 /> : <Group11 />}
                </button>
              )}
            </div>
            {confirmPasswordEmpty && <p className="tw-text-[#e88181] tw-text-sm tw-mt-1">Confirm Password cannot be empty.</p>}
            {createAndConfirmPasswordMismatch && <p className="tw-text-[#e88181] tw-text-sm tw-mt-1">Passwords do not match.</p>}
            {incorrectTemporaryPassword && <p className="tw-text-[#e88181] tw-text-sm tw-mt-1">Temporary password has expired.</p>}
          </div>

          {/* Submit Button */}
          <button
            type="button"
            onClick={passwordCreationHandler}
            disabled={isLoading}
            className="tw-w-full tw-h-12 tw-bg-[#03234a] tw-text-[#d0fb56] tw-font-bold tw-rounded-md hover:tw-bg-[#021833] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-all tw-shadow-md active:tw-transform active:tw-scale-[0.98] tw-flex tw-items-center tw-justify-center"
          >
            {isLoading ? <CustomLoader variant="blue" /> : "PROCEED"}
          </button>
        </div>

        {/* Footer */}
        <div className="tw-mt-auto tw-absolute tw-bottom-4 tw-text-xs tw-text-[#03234a] tw-flex tw-items-center">
          <span>©2025 All Rights Reserved</span>
          <span className="tw-mx-2">|</span>
          <a
            href="https://wadeinsight.com/privacy-policy/"
            target="_blank"
            rel="noreferrer"
            className="hover:tw-underline"
          >
            Privacy Policy
          </a>
        </div>
      </div>

      <PasswordChangeMessage
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          navigate("/");
        }}
      />
    </AuthLayout>
  );
};

export default ChangePassword;
